import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { getApiUrl } from '@/lib/env';
import type { JWTToken } from '@/types/branded';
import { createJWTToken } from '@/types/branded';

import { tokenManager } from './token-manager';
import type { ApiError, ApiErrorCode, ApiResult } from './types';
import {
  createApiError,
  createApiSuccess,
  getErrorCodeFromStatus,
} from './types';

const BASE_URL = getApiUrl();
const TIMEOUT = 30000;

type FailedRequest = {
  resolve: (token: JWTToken) => void;
  reject: (error: Error) => void;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (
  error: Error | null,
  token: JWTToken | null = null
): void => {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else if (token) {
      request.resolve(token);
    }
  });
  failedQueue = [];
};

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const isAuthEndpoint = originalRequest.url?.includes('/auth/');
    const is401 = error.response?.status === 401;

    if (is401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({
            resolve: (token: JWTToken) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: Error) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const currentToken = tokenManager.getToken();
        if (!currentToken) {
          throw new Error('No token available for refresh');
        }

        const response = await axios.post<{ token: string }>(
          `${BASE_URL}/auth/refresh`,
          {
            token: currentToken,
          }
        );

        const newToken = createJWTToken(response.data.token);
        tokenManager.setToken(newToken);
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        tokenManager.clearToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function extractErrorMessage(error: AxiosError): string {
  const data = error.response?.data as
    | { message?: string | string[] }
    | undefined;

  if (data?.message) {
    if (Array.isArray(data.message)) {
      return data.message.join(', ');
    }
    return data.message;
  }

  if (error.message === 'Network Error') {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  return error.message || 'An unexpected error occurred';
}

function getErrorCode(error: AxiosError): ApiErrorCode {
  if (error.message === 'Network Error') {
    return 'NETWORK_ERROR';
  }

  const status = error.response?.status;
  if (status) {
    return getErrorCodeFromStatus(status);
  }

  return 'UNKNOWN';
}

export async function apiRequest<T>(
  request: () => Promise<AxiosResponse<T>>
): Promise<ApiResult<T>> {
  try {
    const response = await request();
    return createApiSuccess(response.data);
  } catch (error) {
    const axiosError = error as AxiosError;
    const message = extractErrorMessage(axiosError);
    const code = getErrorCode(axiosError);
    const statusCode = axiosError.response?.status || 0;

    return createApiError(message, code, statusCode);
  }
}

export function createApiErrorFromAxios(error: AxiosError): ApiError {
  return {
    message: extractErrorMessage(error),
    code: getErrorCode(error),
    statusCode: error.response?.status || 0,
  };
}
